import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import { SegButton } from "../../../SegUI/SegButton/SegButton";
import { SegSlider } from "../../../SegUI/SegSlider/SegSlider";
import { imagesCompanias } from "../../../../Assets/imageCompaniasMoto";
import { EmpresaProps } from "../../../../Services/Providers/useConfiguracionEmpresa";
import { useCotizacionStore } from "../../../../Services/Providers/CotizacionStore";
import { useConfig } from "../../../../context/ConfigContext";
import { BloqueVehiculo, RubroVehiculo } from "./BloqueVehiculo";
import {
  CampoCotizacion,
  ValoresCotizacion,
  validarCampo,
  valoresIniciales,
} from "./validaciones";
import { getPasoDatos } from "../../../../utils/getRubro";

interface VehiculoCotizacionProps {
  rubro: RubroVehiculo;
  config: EmpresaProps;
}

const CAMPOS: CampoCotizacion[] = ["marca", "modelo", "anio", "version"];

const Contenedor = styled.div`
  background: #f6f8fb;
  padding-bottom: 32px;
`;

const Columna = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 24px 16px 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Titulo = styled.div`
  h2 {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0 0 4px;
  }
  p {
    margin: 0;
    font-size: 14.5px;
    color: #4a5468;
  }
`;

/**
 * Primer paso del cotizador de Moto: la moto entera en una pantalla
 * (marca, modelo, año y versión encadenados) en vez de cuatro pantallas.
 * Moto no tiene paso de patente, así que este es el reemplazo del InicioNormal
 * que usa Auto.
 */
export const VehiculoCotizacion = ({
  rubro,
  config,
}: VehiculoCotizacionProps) => {
  const nav = useNavigate();
  const { compania } = useParams();
  const { config: envConf } = useConfig();
  const { cotizacionValues, setCotizacionValues } = useCotizacionStore();
  const formRef = useRef<HTMLFormElement | null>(null);

  const isUY = envConf.pais === "UY";
  const pais = envConf.pais;
  const adaptativeMargin = `${10 / (window?.devicePixelRatio || 1)}vh`;

  const [valores, setValores] = useState<ValoresCotizacion>(valoresIniciales);
  const [errores, setErrores] = useState<
    Partial<Record<CampoCotizacion, string>>
  >({});

  useEffect(() => {
    setValores((prev) => ({
      ...prev,
      marca: cotizacionValues.marca,
      modelo: cotizacionValues.modelo,
      anio: cotizacionValues.año,
      version: cotizacionValues.version,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cambiar = (parcial: Partial<ValoresCotizacion>) => {
    const siguiente = { ...valores, ...parcial };
    setValores(siguiente);
    setErrores((prev) => {
      const actualizados = { ...prev };
      (Object.keys(parcial) as CampoCotizacion[]).forEach((campo) => {
        if (!actualizados[campo]) return;
        const mensaje = validarCampo(campo, siguiente, { isUY });
        if (mensaje) actualizados[campo] = mensaje;
        else delete actualizados[campo];
      });
      return actualizados;
    });
  };

  const listos = CAMPOS.filter(
    (campo) => !validarCampo(campo, valores, { isUY })
  ).length;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const pendientes: Partial<Record<CampoCotizacion, string>> = {};
    CAMPOS.forEach((campo) => {
      const mensaje = validarCampo(campo, valores, { isUY });
      if (mensaje) pendientes[campo] = mensaje;
    });
    setErrores(pendientes);

    const primero = CAMPOS.find((campo) => pendientes[campo]);
    if (primero) {
      const nodo = formRef.current?.querySelector<HTMLElement>(
        `[data-campo="${primero}"]`
      );
      nodo?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setCotizacionValues({
      ...cotizacionValues,
      marca: valores.marca,
      modelo: valores.modelo,
      año: valores.anio,
      version: valores.version,
    });
    nav(getPasoDatos(rubro, compania));
  };

  return (
    <Contenedor>
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <Columna>
          <Titulo>
            <h2>¿Cuál es tu moto?</h2>
            <p>
              Con la marca, el modelo, el año y la versión ya podemos empezar a
              buscar precios.
            </p>
          </Titulo>

          <BloqueVehiculo
            rubro={rubro}
            config={config}
            isUY={isUY}
            modo="manual"
            valores={valores}
            errores={errores}
            onCambio={cambiar}
          />

          <div className="text-center">
            <SegButton
              label="Continuar"
              type="submit"
              disabled={listos < CAMPOS.length}
              bgColor={
                config.origen !== "SEGURARSE" ? config.colors.primary : undefined
              }
              hoverBgColor={
                config.origen !== "SEGURARSE" ? config.colors.primary : undefined
              }
              hoverBorderColor={
                config.origen !== "SEGURARSE" ? config.colors.accent : undefined
              }
              hoverOpacity={config.origen !== "SEGURARSE"}
            />
          </div>
        </Columna>

        <div className="slide-cias" style={{ marginTop: adaptativeMargin }}>
          <SegSlider
            companias={
              config.origen === "SEGURARSE" && pais === "AR"
                ? imagesCompanias.filter((comp) => comp.pais?.includes("AR"))
                : []
            }
          />
        </div>
      </form>
    </Contenedor>
  );
};
